import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { examsApi } from '../../api/exams'
import Header from '../../components/layout/Header'
import Footer from '../../components/layout/Footer'
import Loading from '../../components/common/Loading'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import { getStorageUrl } from '../../utils/constants'
import './ExamResult.css'

/** Renders a single question with correct answer and user answer - like the app */
function QuestionResultCard({ questionNumber, questionData }) {
  const question = questionData.question || ''
  const type = questionData.type || 'multiple_choice'
  const correctAnswer = questionData.correct_answer ?? ''
  const userAnswer = questionData.user_answer ?? null
  const isCorrect = questionData.is_correct === true
  const points = questionData.points ?? 1
  const earnedPoints = questionData.earned_points ?? (isCorrect ? points : 0)
  const options = questionData.options || []

  const getOptionText = (opt) => {
    if (typeof opt === 'object' && opt !== null) return opt.option ?? opt.text ?? ''
    return String(opt ?? '')
  }

  const getOptionImage = (opt) => {
    if (typeof opt === 'object' && opt !== null) return opt.image || null
    return null
  }

  return (
    <Card className="question-result-card">
      <div className="question-result-header">
        <span className="question-result-number">سؤال {questionNumber}</span>
        <span className={`question-result-badge ${isCorrect ? 'correct' : 'incorrect'}`}>
          {earnedPoints} / {points} نقطة
        </span>
      </div>
      <p className="question-result-text">{question}</p>
      {questionData.question_image && (
        <div className="question-result-image-wrap">
          <img src={getStorageUrl(questionData.question_image)} alt="" className="question-result-image" />
        </div>
      )}

      {type === 'multiple_choice' && options.length > 0 && (
        <div className="question-result-options">
          <span className="question-result-options-label">الخيارات:</span>
          {options.map((opt, i) => {
            const optionText = getOptionText(opt)
            const optionImage = getOptionImage(opt)
            const isCorrectOption = optionText === correctAnswer
            const isUserAnswer = optionText === userAnswer
            return (
              <div
                key={i}
                className={`question-result-option ${isCorrectOption ? 'correct' : ''} ${isUserAnswer && !isCorrectOption ? 'wrong' : ''}`}
              >
                <span className="option-label">{String.fromCharCode(65 + i)}</span>
                <div className="option-content">
                  {optionImage && (
                    <div className="option-image-wrap">
                      <img src={getStorageUrl(optionImage)} alt="" className="option-image" />
                    </div>
                  )}
                  <span className="option-text">{optionText}</span>
                </div>
                {isCorrectOption && <span className="option-icon correct">✓</span>}
                {isUserAnswer && !isCorrectOption && <span className="option-icon wrong">✗</span>}
              </div>
            )
          })}
          <div className="question-result-summary">
            <div className="summary-row correct">
              <span className="summary-icon">✓</span>
              <span>الإجابة الصحيحة: {correctAnswer}</span>
            </div>
            {userAnswer != null && (
              <div className={`summary-row ${userAnswer === correctAnswer ? 'correct' : 'wrong'}`}>
                <span className="summary-icon">{userAnswer === correctAnswer ? '✓' : '✗'}</span>
                <span>إجابتك: {userAnswer}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {type === 'multiple_choice' && options.length === 0 && (
        <div className="question-result-summary">
          <div className="summary-row correct">
            <span className="summary-icon">✓</span>
            <span>الإجابة الصحيحة: {correctAnswer}</span>
          </div>
          {userAnswer != null && (
            <div className={`summary-row ${userAnswer === correctAnswer ? 'correct' : 'wrong'}`}>
              <span className="summary-icon">{userAnswer === correctAnswer ? '✓' : '✗'}</span>
              <span>إجابتك: {userAnswer}</span>
            </div>
          )}
        </div>
      )}

      {(type === 'true_false' || type === 'short_answer') && (
        <div className="question-result-summary">
          <div className="summary-row correct">
            <span className="summary-icon">✓</span>
            <span>الإجابة الصحيحة: {type === 'true_false' ? (correctAnswer === 'true' ? 'صح' : 'خطأ') : correctAnswer}</span>
          </div>
          <div className={`summary-row ${isCorrect ? 'correct' : 'wrong'}`}>
            <span className="summary-icon">{isCorrect ? '✓' : '✗'}</span>
            <span>إجابتك: {type === 'true_false' ? (userAnswer === 'true' ? 'صح' : (userAnswer === 'false' ? 'خطأ' : (userAnswer || 'لم تجب'))) : (userAnswer || 'لم تجب')}</span>
          </div>
        </div>
      )}
    </Card>
  )
}

const ExamResult = () => {
  const { id } = useParams()

  const { data, isLoading } = useQuery({
    queryKey: ['exam-result', id],
    queryFn: () => examsApi.getExamResult(id),
  })

  if (isLoading) {
    return (
      <>
        <Header />
        <Loading fullScreen text="جاري التحميل..." />
        <Footer />
      </>
    )
  }

  const result = data?.data

  if (!result) {
    return (
      <>
        <Header />
        <div className="error-container">
          <p>لا توجد نتائج</p>
        </div>
        <Footer />
      </>
    )
  }

  // Get score values - ensure they're numbers
  const score = Number(result.score) || 0
  const totalScore = Number(result.total_score) || 0
  const percentage = result.percentage != null ? Number(result.percentage) : (totalScore > 0 ? Math.round((score / totalScore) * 100 * 100) / 100 : 0)
  const passed = result.is_passed || result.passed || false
  
  // Calculate correct answers count from questions if not provided
  const correctAnswersCount = result.correct_answers || 
    (result.questions ? result.questions.filter(q => q.is_correct === true).length : 0)
  const totalQuestionsCount = result.total_questions || 
    (result.questions ? result.questions.length : 0)

  const questions = result.questions || []

  return (
    <div className="exam-result-page bg-gray-900 min-h-screen">
      <Header />
      <main className="exam-result-main bg-gray-900">
        <div className="exam-result-layout container mx-auto px-4">
          {/* Result card - appears right on desktop, top on mobile */}
          <aside className="exam-result-side">
            <Card className="result-card">
              <div className={`result-header ${passed ? 'passed' : 'failed'}`}>
                <h1 className="result-title">
                  {passed ? 'مبروك! نجحت في الامتحان' : 'لم تنجح في الامتحان'}
                </h1>
                <div className="result-score">
                  <span className="score-value">{percentage}%</span>
                  <span className="score-label">الدرجة</span>
                </div>
              </div>

              <div className="result-details">
                <div className="detail-item">
                  <span className="detail-label">الدرجة الكلية:</span>
                  <span className="detail-value">{score} / {totalScore}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">الإجابات الصحيحة:</span>
                  <span className="detail-value">{correctAnswersCount} / {totalQuestionsCount}</span>
                </div>
                {result.exam?.passing_score && (
                  <div className="detail-item">
                    <span className="detail-label">الدرجة المطلوبة:</span>
                    <span className="detail-value">{result.exam.passing_score}%</span>
                  </div>
                )}
              </div>

              <div className="result-actions">
                <Link to="/exams">
                  <Button variant="outline">العودة للامتحانات</Button>
                </Link>
                {!passed && (
                  <Link to={`/exams/${id}/take`}>
                    <Button variant="primary">إعادة المحاولة</Button>
                  </Link>
                )}
              </div>
            </Card>
          </aside>

          {/* Answers - appears left on desktop, below result on mobile */}
          <div className="exam-result-answers">
            {questions.length > 0 ? (
              <>
                <h2 className="questions-review-title">مراجعة الإجابات</h2>
                <div className="questions-review-list">
                  {questions.map((q, index) => (
                    <QuestionResultCard
                      key={q.id}
                      questionNumber={index + 1}
                      questionData={q}
                    />
                  ))}
                </div>
                <div className="result-actions result-actions-bottom">
                  <Link to="/exams">
                    <Button variant="primary">تم</Button>
                  </Link>
                </div>
              </>
            ) : (
              <div className="questions-review-placeholder" />
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default ExamResult

